import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/features/auth";
import { checkUserFromRole } from "@/helpers";
import { Can } from "@/hooks/useCan/Can";

/**
 * Reusable image slider component
 * Props:
 * - slides: Array<{
 *     id?: string|number,
 *     image?: string,
 *     imageAlt?: string,
 *     title?: string,
 *     subtitle?: string,
 *     description?: string | string[],
 *     text?: string | string[],
 *     textLines?: string[],
 *     paragraphs?: string[],
 *     sections?: Array<{ id?: string|number, title?: string, description?: string | string[], text?: string | string[], textLines?: string[] }>,
 *     layout?: 'overlay' | 'split',
 *     imagePosition?: 'left' | 'right',
 *     backgroundClass?: string,
 *     outerBackgroundClass?: string,
 *     contentClassName?: string,
 *     imageClassName?: string,
 *     heightClass?: string,
 *     minHeightClass?: string,
 *     titleAlign?: 'left' | 'center',
 *     kicker?: string,
 *     kickerClassName?: string,
 *     badges?: string[],
 *     badgeClassName?: string,
 *     titlePlacement?: 'content' | 'center',
 *     titleColorClass?: string,
 *     contentBackgroundClass?: string,
 *     cta?: { label?: string, onClick?: () => void, className?: string },
 *   }>
 * - autoPlay?: boolean (default true)
 * - intervalMs?: number (default 4000)
 * - pauseOnHover?: boolean (default true)
 * - initialIndex?: number (default 0)
 * - heightClass?: string (default 'h-64 sm:h-80 md:h-96 lg:h-[500px]')
 * - showPlayPause?: boolean (default false)
 * - showIndicators?: boolean (default true)
 * - showArrows?: boolean (default true)
 * - containerClass?: string (extra classes for outer container)
 * - progressColorClass?: string (default gradient)
 * - showExplore?: boolean (default true)
 * - exploreLabel?: string (default 'Explore Feature')
 * - onExplore?: () => void
 * - onSlideChange?: (index:number) => void
 * - fallbackImage?: string (default '/logos/logo.svg')
 * - defaultLayout?: 'overlay' | 'split' (default 'overlay')
 */
export default function ImageSlider({
  slides = [],
  autoPlay = true,
  intervalMs = 10000,
  pauseOnHover = true,
  initialIndex = 0,
  heightClass = "h-64 sm:h-80 md:h-96 lg:h-[500px]",
  showPlayPause = false,
  showIndicators = true,
  showArrows = true,
  containerClass = "",
  progressColorClass = "bg-gradient-to-r from-nxstart-blue to-nxstart-green",
  showExplore = true,
  exploreLabel = "Explore",
  onExplore = () => {},
  onSlideChange,
  fallbackImage = "/logos/logo.svg",
  defaultLayout = "overlay",
}) {
  const [currentSlide, setCurrentSlide] = useState(
    Math.min(Math.max(initialIndex, 0), Math.max(slides.length - 1, 0)),
  );
  const [isPlaying, setIsPlaying] = useState(Boolean(autoPlay));
  const [progress, setProgress] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const intervalRef = useRef(null);
  const progressIntervalRef = useRef(null);

  const hasSlides = Array.isArray(slides) && slides.length > 0;
  const { user, userRole } = useAuthStore();
  const permissions = user?.permissions || [];
  const isUserLevel = checkUserFromRole(userRole);

  const toArray = (value) => {
    if (Array.isArray(value)) {
      return value.filter(
        (item) => typeof item === "string" && item.trim().length > 0,
      );
    }
    if (typeof value === "string" && value.trim().length > 0) {
      return [value.trim()];
    }
    return [];
  };

  const nextSlide = () => {
    if (!hasSlides) return;
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setProgress(0);
  };

  const prevSlide = () => {
    if (!hasSlides) return;
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setProgress(0);
  };

  const goToSlide = (index) => {
    if (!hasSlides) return;
    setCurrentSlide(Math.max(0, Math.min(index, slides.length - 1)));
    setProgress(0);
  };

  const togglePlayPause = () => setIsPlaying((p) => !p);

  const handleTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
  const handleTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    if (isLeftSwipe) nextSlide();
    if (isRightSwipe) prevSlide();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowLeft") prevSlide();
    if (e.key === "ArrowRight") nextSlide();
    if (e.key === " ") {
      e.preventDefault();
      togglePlayPause();
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, []);

  useEffect(() => {
    setCurrentSlide((prev) =>
      Math.min(Math.max(prev, 0), Math.max(slides.length - 1, 0)),
    );
  }, [slides.length]);

  useEffect(() => {
    if (!hasSlides) return undefined;
    if (isPlaying) {
      intervalRef.current = setInterval(nextSlide, intervalMs);
      progressIntervalRef.current = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 100) return 0;
          return prevProgress + (50 / intervalMs) * 100; // every 50ms
        });
      }, 50);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (progressIntervalRef.current)
        clearInterval(progressIntervalRef.current);
    };
  }, [isPlaying, intervalMs, hasSlides]);

  useEffect(() => {
    if (typeof onSlideChange === "function") onSlideChange(currentSlide);
    setProgress(0);
  }, [currentSlide]);

  if (!hasSlides) {
    return (
      <div className={`w-full bg-card p-8 text-center ${containerClass}`}>
        <p className="text-sm text-muted-foreground">No slides to display.</p>
      </div>
    );
  }

  const activeSlide = slides[currentSlide] || {};
  const activeLayout = activeSlide.layout || defaultLayout;
  const containerHeightClass =
    activeLayout === "split"
      ? activeSlide.heightClass || "min-h-[480px]"
      : activeSlide.heightClass || heightClass;
  const outerBackgroundClass = "bg-background";
  const isSplitLayout = activeLayout === "split";

  const playPauseButtonClass = isSplitLayout
    ? "bg-card text-foreground hover:bg-muted shadow-md focus:ring-ring/40"
    : "bg-card/80 backdrop-blur-sm hover:bg-card text-foreground focus:ring-ring/40";
  const exploreButtonClass = isSplitLayout
    ? "px-3 py-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-md focus:ring-ring/40"
    : "px-3 py-1.5 rounded-full bg-card text-foreground hover:bg-muted shadow-md focus:ring-ring/40";
  const progressTrackClass = isSplitLayout ? "bg-border" : "bg-border";

  const frameClassName = [
    "relative",
    containerHeightClass,
    "overflow-hidden",
    // 'rounded-radius',
    "transition-colors",
    "duration-500",
    isSplitLayout
      ? activeSlide.frameClassName || "bg-transparent"
      : activeSlide.frameClassName || "bg-background",
  ]
    .filter(Boolean)
    .join(" ");

  const outerClassName = [
    "relative",
    "w-full",
    "shadow-2xl dark:shadow-black/40",
    "group",
    containerClass,
    outerBackgroundClass,
    isSplitLayout ? "overflow-visible" : "overflow-hidden",
  ]
    .filter(Boolean)
    .join(" ");

  const renderOverlayLayout = (slide, isActive, index) => {
    const imageSrc = slide.image || fallbackImage;
    const imageAlt = slide.imageAlt || slide.title || `Slide ${index + 1}`;
    const paragraphs = toArray(slide.description);

    const hasTextLines =
      Array.isArray(slide.textLines) && slide.textLines.length > 0;
    const hasParagraphs = !hasTextLines && paragraphs.length > 0;
    const overlayTitleColorClass = slide.titleColorClass || "text-white";

    return (
      <>
        <img
          src={imageSrc}
          alt={imageAlt}
          className="w-full h-full object-cover"
          loading={index <= 1 ? "eager" : "lazy"}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = fallbackImage;
          }}
        />

        {/* <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" /> */}

        {(slide.title || hasTextLines || hasParagraphs) && (
          <div className="absolute bottom-8 left-8 right-8 text-white">
            {slide.title && (
              <h2
                className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-2 transition-all duration-700 ${overlayTitleColorClass} ${
                  isActive
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-2"
                }`}
              >
                {slide.title}
              </h2>
            )}

            {hasTextLines && (
              <div className="space-y-2">
                {slide.textLines.map((line, lineIndex) => (
                  <p
                    key={lineIndex}
                    className={`text-sm sm:text-base md:text-lg opacity-90 transition-all duration-700 ${
                      isActive
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-2"
                    }`}
                    style={{ transitionDelay: `${(lineIndex + 1) * 100}ms` }}
                  >
                    {line}
                  </p>
                ))}
              </div>
            )}

            {hasParagraphs && (
              <div className="space-y-2">
                {paragraphs.map((line, lineIndex) => (
                  <p
                    key={lineIndex}
                    className={`text-sm sm:text-base md:text-lg opacity-90 transition-all duration-700 ${
                      isActive
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-2"
                    }`}
                    style={{ transitionDelay: `${(lineIndex + 1) * 100}ms` }}
                  >
                    {line}
                  </p>
                ))}
              </div>
            )}

            <div
              className="mt-6 h-1 bg-gradient-to-r from-white to-transparent rounded-full"
              style={{
                maxWidth: "120px",
                width: isActive ? "120px" : "0px",
                transition: "width 700ms ease",
                transitionDelay: "600ms",
              }}
            />
          </div>
        )}
      </>
    );
  };

  const renderSplitLayout = (slide, isActive, index) => {
    const imageSrc = slide.image || fallbackImage;
    const imageAlt = slide.imageAlt || slide.title || `Slide ${index + 1}`;
    const imagePosition = slide.imagePosition === "right" ? "right" : "left";
    const paragraphs = [
      ...toArray(slide.description),
      ...toArray(slide.text),
      ...toArray(slide.textLines),
      ...toArray(slide.paragraphs),
    ];
    const sections = Array.isArray(slide.sections) ? slide.sections : [];
    const badges = Array.isArray(slide.badges) ? slide.badges : [];
    const kicker = typeof slide.kicker === "string" ? slide.kicker : null;
    const kickerClassName =
      slide.kickerClassName ||
      "text-xs font-semibold uppercase tracking-[0.3em] text-secondary";
    const titleColorClass = slide.titleColorClass || "text-foreground";
    const titlePlacement =
      slide.titlePlacement === "center" ? "center" : "content";
    const showHeaderTitle =
      titlePlacement === "center" && (kicker || slide.title);
    const headerWrapperClassName =
      slide.headerWrapperClassName || "text-foreground";
    const headerTitleClassName =
      slide.headerTitleClassName ||
      "text-lg sm:text-xl md:text-2xl font-semibold";
    const headerSubtitleClassName = slide.headerSubtitleClassName || "";
    const subtitleClassName =
      slide.subtitleClassName || "text-muted-foreground";
    const showSubtitleInHeader = showHeaderTitle && Boolean(slide.subtitle);
    const showKickerInContent = kicker && !showHeaderTitle;
    const showTitleInContent = slide.title && !showHeaderTitle;
    const showSubtitleInContent = slide.subtitle && !showSubtitleInHeader;
    const titleAlign =
      slide.titleAlign === "center" ? "text-center md:text-left" : "text-left";
    const imageOrderClass =
      imagePosition === "right" ? "md:order-2" : "md:order-1";
    const contentOrderClass =
      imagePosition === "right" ? "md:order-1" : "md:order-2";
    const contentAnimationClass = isActive
      ? "opacity-100 translate-y-0"
      : "opacity-0 translate-y-6";
    const imageAnimationClass = isActive
      ? "opacity-100 scale-100 translate-y-0"
      : "opacity-0 scale-95 translate-y-6";
    const minHeightClass = slide.minHeightClass || "h-full";
    const ctaConfig =
      slide.cta && typeof slide.cta === "object" ? slide.cta : null;
    const handleCtaClick =
      ctaConfig && typeof ctaConfig.onClick === "function"
        ? ctaConfig.onClick
        : onExplore;
    const gridPaddingClassName = [
      "grid h-full items-stretch gap-6 px-4 py-6 md:grid-cols-2 md:px-6 md:py-8",
      slide.innerGridClassName || "",
    ]
      .filter(Boolean)
      .join(" ");
    const contentBackgroundClass =
      slide.contentBackgroundClass ||
      "rounded-radius border border-border bg-background px-6 py-8 text-lg shadow-lg";
    const contentContainerClass = [
      "flex h-full flex-col gap-6 text-foreground",
      contentOrderClass,
      "transition-all duration-700",
      contentAnimationClass,
      contentBackgroundClass,
      slide.contentClassName || "",
    ]
      .filter(Boolean)
      .join(" ");
    const headerTitleClasses = [
      "leading-tight",
      headerTitleClassName,
      titleColorClass,
    ]
      .filter(Boolean)
      .join(" ");
    const contentTitleClasses = [
      "text-lg sm:text-xl md:text-2xl font-semibold",
      titleColorClass,
      titleAlign,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        className={`flex h-full w-full items-stretch justify-center px-4 py-6 ${minHeightClass}`}
      >
        <div
          className={`mx-auto flex h-full w-full flex-col overflow-hidden rounded-radius`}
        >
          <div className="flex h-full flex-col">
            {showHeaderTitle && (
              <div className={`mb-5 text-center ${headerWrapperClassName}`}>
                {kicker && (
                  <span className={`${kickerClassName} block text-center`}>
                    {kicker}
                  </span>
                )}
                {slide.title && (
                  <h3 className={headerTitleClasses}>{slide.title}</h3>
                )}
                {showSubtitleInHeader && (
                  <p
                    className={`mx-auto max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg ${headerSubtitleClassName || subtitleClassName}`}
                  >
                    {slide.subtitle}
                  </p>
                )}
              </div>
            )}

            {/* Main Slider Context */}
            <div className={gridPaddingClassName}>
              {/* Image Slider Context */}
              <div
                className={`flex h-full items-center justify-center overflow-hidden rounded-radius border border-border bg-card px-6 py-8 shadow-lg transition-all duration-700 ${imageOrderClass} ${imageAnimationClass} ${slide.imageWrapperClassName || ""}`}
              >
                <img
                  src={imageSrc}
                  alt={imageAlt}
                  className={`absolute inset-0 h-full w-full rounded-radius object-cover shadow-lg ${slide.imageClassName || ""}`}
                  loading={index <= 1 ? "eager" : "lazy"}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = fallbackImage;
                  }}
                />
              </div>

              {/* Text Slider Context */}
              <div className={contentContainerClass}>
                {showKickerInContent && (
                  <span className={kickerClassName}>{kicker}</span>
                )}

                {showTitleInContent && (
                  <h3 className={contentTitleClasses}>{slide.title}</h3>
                )}
                {showSubtitleInContent && (
                  <p
                    className={`text-base leading-relaxed text-muted-foreground md:text-lg ${subtitleClassName}`}
                  >
                    {slide.subtitle}
                  </p>
                )}

                {badges.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {badges.map((badge, badgeIndex) => (
                      <span
                        key={badge ?? badgeIndex}
                        className={`rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary ${slide.badgeClassName || ""}`}
                      >
                        {badge}
                      </span>
                    ))}
                  </div>
                )}

                {paragraphs.length > 0 && (
                  <div className="space-y-3 leading-relaxed text-muted-foreground">
                    {paragraphs.map((text, textIndex) => (
                      <p key={textIndex}>{text}</p>
                    ))}
                  </div>
                )}

                {sections.length > 0 && (
                  <div className="space-y-4">
                    {sections.map((section, sectionIndex) => {
                      const sectionParagraphs = [
                        ...toArray(section?.description),
                        ...toArray(section?.text),
                        ...toArray(section?.textLines),
                      ];
                      return (
                        <div
                          key={section?.id ?? section?.title ?? sectionIndex}
                          className="space-y-2"
                        >
                          {section?.title && (
                            <h3 className="text-base font-semibold text-primary">
                              {section.title}
                            </h3>
                          )}
                          {sectionParagraphs.map((line, lineIndex) => (
                            <p
                              key={lineIndex}
                              className={`leading-relaxed text-muted-foreground ${section.desClass}`}
                            >
                              {line}
                            </p>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}

                {ctaConfig && (
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={handleCtaClick}
                      className={`inline-flex items-center justify-center rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring/40 ${ctaConfig.className || ""}`}
                    >
                      {ctaConfig.label || "Learn more"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={outerClassName}
      onMouseEnter={() => pauseOnHover && setIsPlaying(false)}
      onMouseLeave={() => pauseOnHover && setIsPlaying(Boolean(autoPlay))}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Main Slider Container */}
      <div className={frameClassName}>
        {slides.map((slide, index) => {
          const isActive = index === currentSlide;
          const slideLayout = slide?.layout || defaultLayout;
          return (
            <div
              key={slide?.id ?? index}
              className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                isActive
                  ? "pointer-events-auto scale-100 opacity-100"
                  : "pointer-events-none scale-105 opacity-0"
              }`}
              aria-hidden={isActive ? "false" : "true"}
            >
              {slideLayout === "split"
                ? renderSplitLayout(slide || {}, isActive, index)
                : renderOverlayLayout(slide || {}, isActive, index)}
            </div>
          );
        })}

        {/* Top-right Controls */}
        {(showPlayPause || showExplore) && (
          <div className="absolute right-4 top-4 flex items-center gap-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            {showPlayPause && (
              <button
                onClick={togglePlayPause}
                className={`${playPauseButtonClass} rounded-full p-2 transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2`}
                aria-label={isPlaying ? "Pause slideshow" : "Play slideshow"}
              >
                {isPlaying ? <Pause size={20} /> : <Play size={20} />}
              </button>
            )}

            {showExplore && (
              <Can
                resource="company_information"
                action="update"
                enabled={!isUserLevel}
                userPermissions={permissions}
              >
                <button
                  onClick={onExplore}
                  className={`${exploreButtonClass} transition-all duration-300 focus:outline-none focus:ring-2`}
                >
                  {exploreLabel}
                </button>
              </Can>
            )}
          </div>
        )}

        {/* Progress Bar */}
        <div
          className={`absolute bottom-0 left-0 h-1 w-full overflow-hidden rounded-full ${progressTrackClass}`}
        >
          <div
            className={`h-full transition-all duration-100 ease-linear ${progressColorClass}`}
            style={{ width: `${Math.max(0, Math.min(progress, 100))}%` }}
          />
        </div>
      </div>

      {/* Navigation Controls */}
      {(showArrows || showIndicators) && (
        <div className="m-0 flex items-center border-t border-border bg-background p-3">
          {showArrows ? (
            <div className="flex flex-1 justify-start">
              {currentSlide > 0 && (
                <Button
                  variant="outline"
                  className="rounded-radius"
                  onClick={prevSlide}
                  aria-label="Previous slide"
                >
                  <ChevronLeft size={20} />
                  <span className="font-medium">Previous</span>
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-1" />
          )}

          {showIndicators && (
            <div className="flex flex-none items-center justify-center space-x-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`h-6 w-6 rounded-radius font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-ring/40 ${
                    index === currentSlide
                      ? "scale-110 bg-primary text-primary-foreground shadow-lg"
                      : "bg-background text-muted-foreground shadow-md hover:scale-105 hover:bg-muted hover:text-foreground hover:shadow-lg"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          )}

          {/* Orginal Code */}
          {/* {showArrows ? (
            <div className="flex flex-1 justify-end">
              <Button
                onClick={() =>
                  currentSlide === slides.length - 1 ? onExplore() : nextSlide()
                }
                className="rounded-radius"
                aria-label={
                  currentSlide === slides.length - 1 ? "Explore" : "Next slide"
                }
              >
                <span className="font-medium">
                  {currentSlide === slides.length - 1 ? "Explore" : "Next"}
                </span>
                <ChevronRight size={20} />
              </Button>
            </div>
          ) : (
            <div className="flex flex-1" />
          )} */}
          {showArrows ? (
            <div className="flex flex-1 justify-end">
              {currentSlide === slides.length - 1 ? (
                <Can
                  resource="onboard_setup"
                  action="create"
                  enabled={!isUserLevel}
                  userPermissions={permissions}
                >
                  {/* Explore button only on last slide */}
                  <Button
                    onClick={onExplore}
                    className="rounded-radius"
                    aria-label="Explore"
                  >
                    <span className="font-medium">Explore</span>
                    <ChevronRight size={20} />
                  </Button>
                </Can>
              ) : (
                // Next button on other slides
                <Button
                  onClick={nextSlide}
                  className="rounded-radius"
                  aria-label="Next slide"
                >
                  <span className="font-medium">Next</span>
                  <ChevronRight size={20} />
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-1" />
          )}
        </div>
      )}
    </div>
  );
}
